import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Spin,
  Badge,
  Tooltip,
  Modal,
  theme,
  Alert,
  Tabs,
  Empty,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  DollarOutlined,
  WalletOutlined,
  CalendarOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  UserOutlined,
  BankOutlined,
  ShopOutlined,
  SettingOutlined,
  StopOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const SERVER_TIMEZONE = "Australia/Sydney"; // AEST/AEDT

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// API helper
const fetchWithAuth = async (url, token, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || `HTTP ${response.status}`);
  }
  return response.json();
};

// Status configuration
const statusConfig = {
  COMPLETED: { color: "success", icon: <CheckCircleOutlined />, text: "Completed" },
  RUNNING: { color: "processing", icon: <SyncOutlined spin />, text: "Running" },
  PENDING: { color: "default", icon: <ClockCircleOutlined />, text: "Pending" },
  FAILED: { color: "error", icon: <CloseCircleOutlined />, text: "Failed" },
  CANCELLED: { color: "warning", icon: <CloseCircleOutlined />, text: "Cancelled" },
};

// Run type configuration
const runTypeConfig = {
  FINAL: { color: "blue", text: "Final" },
  PREVIEW: { color: "cyan", text: "Preview" },
  RERUN: { color: "orange", text: "Rerun" },
  BACKFILL: { color: "purple", text: "Backfill" },
};

// Scope configuration
const scopeConfig = {
  FULL: { color: "geekblue", text: "Full" },
  CONTROL_GROUP: { color: "green", text: "Control Group" },
  VIEW_GROUP: { color: "lime", text: "View Group" },
  CUSTOMER: { color: "gold", text: "Customer" },
};

// Settlement rule type configuration - 4 types per requirements
const ruleTypeConfig = {
  GROUP_REVENUE: {
    key: "group",
    label: "Group Revenue",
    description: "AEMO DUID revenue allocation to control groups",
    icon: <TeamOutlined />,
    color: "blue",
  },
  SYSTEM_ALLOCATION: {
    key: "system",
    label: "System Allocation",
    description: "Distribute group revenue to individual battery systems",
    icon: <SettingOutlined />,
    color: "cyan",
  },
  PARTNER_REVENUE: {
    key: "partner",
    label: "Partner Revenue",
    description: "Partner/company revenue sharing based on onboarded MW",
    icon: <BankOutlined />,
    color: "purple",
  },
  CUSTOMER_PRICING: {
    key: "customer",
    label: "Customer Pricing",
    description: "Customer billing and payout rules",
    icon: <UserOutlined />,
    color: "gold",
  },
};

// Summary stat card component
function SummaryStatCard({ title, value, prefix, suffix, icon, color, loading, extra }) {
  const { token } = theme.useToken();

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>{title}</Text>
          <Statistic
            value={loading ? "—" : value}
            prefix={prefix}
            suffix={suffix}
            valueStyle={{ fontSize: 28, fontWeight: 700, color: token.colorText }}
            loading={loading}
          />
          {extra && <div style={{ marginTop: 8 }}>{extra}</div>}
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: color || "rgba(84, 162, 204, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            color: token.colorPrimary,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

export default function SettlementRuns() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [runs, setRuns] = useState([]);
  const [outstanding, setOutstanding] = useState({});
  const [activeRuleType, setActiveRuleType] = useState("all"); // "all" or one of the rule types
  const [filters, setFilters] = useState({
    dateRange: [dayjs().subtract(30, "day"), dayjs()],
    status: null,
    runType: null,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      // Build query params for date range
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.set("startDate", filters.dateRange[0].format("YYYY-MM-DD"));
        params.set("endDate", filters.dateRange[1].format("YYYY-MM-DD"));
      }
      params.set("limit", "100");

      // Fetch runs and outstanding in parallel
      const [runsRes, outstandingRes] = await Promise.all([
        fetchWithAuth(`/api/settlements/runs?${params.toString()}`, authToken),
        fetchWithAuth("/api/settlements/outstanding", authToken),
      ]);

      setRuns(runsRes.data?.runs || []);
      setOutstanding(outstandingRes.data || {});
    } catch (error) {
      console.error("Failed to fetch settlement runs:", error);
      Modal.error({ title: "Error", content: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.dateRange]);

  // Filter runs based on current filters (status and runType are client-side since backend handles dateRange)
  const filteredRuns = useMemo(() => {
    return runs.filter((run) => {
      // Status filter
      if (filters.status && run.status !== filters.status) {
        return false;
      }
      // Run type filter
      if (filters.runType && run.run_type !== filters.runType) {
        return false;
      }
      return true;
    });
  }, [runs, filters.status, filters.runType]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const todayRuns = runs.filter((r) => r.run_date === today);
    const todayCompleted = todayRuns.filter((r) => r.status === "COMPLETED").length;
    const todayFailed = todayRuns.filter((r) => r.status === "FAILED").length;
    const todayRunning = todayRuns.filter((r) => r.status === "RUNNING").length;

    return {
      todayStatus: todayRunning > 0 ? "RUNNING" : (todayFailed > 0 ? "FAILED" : (todayCompleted > 0 ? "COMPLETED" : "PENDING")),
      todayCount: todayRuns.length,
      lastSuccessfulRun: outstanding.last_successful_run,
    };
  }, [runs, outstanding]);

  const handleViewDetails = (run) => {
    navigate(`/settlements/runs/${run.run_id}`);
  };

  const handleRerun = (run) => {
    Modal.confirm({
      title: "Confirm Rerun",
      content: `Are you sure you want to rerun settlement for ${run.run_date}? This will create a new settlement run with type RERUN.`,
      okText: "Rerun",
      cancelText: "Cancel",
      onOk: async () => {
        setTriggering(true);
        try {
          const authToken = localStorage.getItem("token");
          const result = await fetchWithAuth("/api/settlements/runs/trigger", authToken, {
            method: "POST",
            body: JSON.stringify({
              runDate: run.run_date,
              runScope: run.run_scope || "FULL",
              runType: "RERUN",
              notes: `Rerun of ${run.run_id}`,
            }),
          });
          Modal.success({
            title: "Settlement Rerun Triggered",
            content: `Run ID: ${result.data?.run_id || "Unknown"}`,
          });
          fetchData();
        } catch (error) {
          Modal.error({ title: "Failed to trigger rerun", content: error.message });
        } finally {
          setTriggering(false);
        }
      },
    });
  };

  const handleCancel = (run) => {
    Modal.confirm({
      title: "Cancel Settlement Run",
      content: `Are you sure you want to cancel the settlement run for ${run.run_date}?`,
      okText: "Yes, Cancel",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: async () => {
        try {
          const authToken = localStorage.getItem("token");
          await fetchWithAuth(`/api/settlements/runs/${run.run_id}/cancel`, authToken, {
            method: "PUT",
          });
          Modal.success({ title: "Settlement run cancelled" });
          fetchData();
        } catch (error) {
          Modal.error({ title: "Failed to cancel run", content: error.message });
        }
      },
    });
  };

  const handleDelete = (run) => {
    Modal.confirm({
      title: "Delete Settlement Run",
      content: (
        <div>
          <p>Are you sure you want to delete the settlement run for {run.run_date}?</p>
          <p style={{ color: "#ff4d4f", fontSize: 12 }}>
            This will permanently delete all associated ledger data. This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const authToken = localStorage.getItem("token");
          await fetchWithAuth(`/api/settlements/runs/${run.run_id}`, authToken, {
            method: "DELETE",
          });
          Modal.success({ title: "Settlement run deleted" });
          fetchData();
        } catch (error) {
          Modal.error({ title: "Failed to delete run", content: error.message });
        }
      },
    });
  };

  const handleTriggerManualRun = () => {
    let selectedDate = dayjs().subtract(1, 'day'); // Default to yesterday
    let selectedRunType = 'FINAL';
    let selectedScope = 'FULL';

    Modal.confirm({
      title: "Trigger Manual Settlement Run",
      width: 500,
      content: (
        <div style={{ paddingTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Settlement Date:</Text>
            <DatePicker
              style={{ width: '100%', marginTop: 8 }}
              defaultValue={selectedDate}
              format="YYYY-MM-DD"
              onChange={(date) => { selectedDate = date; }}
              disabledDate={(current) => {
                // Disable future dates
                return current && current > dayjs().endOf('day');
              }}
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Select the date to run settlement for (cannot be future dates)
            </Text>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Run Type:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              defaultValue={selectedRunType}
              onChange={(value) => { selectedRunType = value; }}
              options={[
                { value: 'FINAL', label: 'Final Settlement' },
                { value: 'PREVIEW', label: 'Preview (No Ledger Write)' },
                { value: 'RERUN', label: 'Rerun Settlement' },
              ]}
            />
          </div>

          <div style={{ marginBottom: 8 }}>
            <Text strong>Settlement Scope:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              defaultValue={selectedScope}
              onChange={(value) => { selectedScope = value; }}
              options={[
                { value: 'FULL', label: 'Full Settlement (All Groups)' },
                { value: 'CONTROL_GROUP', label: 'Control Group Only' },
                { value: 'VIEW_GROUP', label: 'View Group Only' },
              ]}
            />
          </div>
        </div>
      ),
      okText: "Run Settlement",
      cancelText: "Cancel",
      onOk: async () => {
        setTriggering(true);
        try {
          const authToken = localStorage.getItem("token");
          const result = await fetchWithAuth("/api/settlements/runs/trigger", authToken, {
            method: "POST",
            body: JSON.stringify({
              runDate: selectedDate.format("YYYY-MM-DD"),
              runScope: selectedScope,
              runType: selectedRunType,
            }),
          });
          Modal.success({
            title: "Settlement Run Triggered",
            content: `Run ID: ${result.data?.run_id || "Unknown"}. The settlement engine will process ${selectedDate.format("YYYY-MM-DD")}.`,
          });
          // Refresh the list
          fetchData();
        } catch (error) {
          Modal.error({ title: "Failed to trigger run", content: error.message });
        } finally {
          setTriggering(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "Run ID",
      dataIndex: "run_id",
      key: "run_id",
      width: 180,
      render: (id, record) => (
        <Text
          code
          style={{ fontSize: 12, cursor: "pointer", color: token.colorPrimary }}
          onClick={() => handleViewDetails(record)}
        >
          {id}
        </Text>
      ),
    },
    {
      title: "Run Time (Server)",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: "descend",
      render: (datetime) => {
        if (!datetime) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        // Database stores UTC - ensure we parse as UTC then convert to server timezone
        let dt = datetime;
        // If no timezone info in string, assume UTC by appending Z
        if (typeof dt === "string" && !dt.endsWith("Z") && !dt.includes("+")) {
          dt = dt.replace(" ", "T") + "Z";
        }
        const serverTime = dayjs(dt).tz(SERVER_TIMEZONE);
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {serverTime.format("YYYY-MM-DD HH:mm:ss")}
          </Text>
        );
      },
    },
    {
      title: "Settlement Date (AEST)",
      dataIndex: "run_date",
      key: "run_date",
      width: 140,
      sorter: (a, b) => dayjs(a.run_date).unix() - dayjs(b.run_date).unix(),
      render: (date) => (
        <Text strong>{dayjs(date).format("YYYY-MM-DD")}</Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "run_type",
      key: "run_type",
      width: 100,
      render: (type) => {
        const config = runTypeConfig[type] || { color: "default", text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const config = statusConfig[status] || { color: "default", icon: null, text: status };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "Processed",
      key: "processed",
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.systems_processed} systems
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.groups_processed} groups, {record.customers_processed} customers
          </Text>
        </Space>
      ),
    },
    {
      title: "Credit",
      dataIndex: "total_credit",
      key: "total_credit",
      width: 120,
      align: "right",
      render: (val) => (
        <Text style={{ color: token.colorSuccess }}>
          ${val?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "—"}
        </Text>
      ),
    },
    {
      title: "Debit",
      dataIndex: "total_debit",
      key: "total_debit",
      width: 120,
      align: "right",
      render: (val) => (
        <Text style={{ color: token.colorError }}>
          ${val?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "—"}
        </Text>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration_seconds",
      key: "duration_seconds",
      width: 100,
      render: (seconds) => {
        if (!seconds) return <Text type="secondary">—</Text>;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return <Text>{mins}m {secs}s</Text>;
      },
    },
    {
      title: "Triggered By",
      dataIndex: "triggered_by",
      key: "triggered_by",
      width: 100,
      render: (by, record) => {
        if (by === "SCHEDULER") {
          return <Tag color="blue">Scheduler</Tag>;
        }
        // For MANUAL:userId, show triggerer_name if available, otherwise "Manual"
        const name = record.triggerer_name || "Manual";
        return <Tag color="orange">{name}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {(record.status === "PENDING" || record.status === "RUNNING") && (
            <Tooltip title="Cancel">
              <Button
                type="text"
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() => handleCancel(record)}
              />
            </Tooltip>
          )}
          {record.status === "FAILED" && (
            <Tooltip title="Rerun">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRerun(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout active="settlementRuns">
      {/* Page Header */}
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Settlement Runs</Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Daily settlement processing history and status. Each run calculates revenue and updates ledger balances.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={handleTriggerManualRun}
          loading={triggering}
        >
          Trigger Manual Run
        </Button>
      </div>

      {/* Info Alert - Connection to Outstanding */}
      <Alert
        message="Settlement Run → Ledger → Outstanding"
        description={
          <span>
            Each successful settlement run generates ledger entries that feed into Outstanding Receivable (platform income pending collection)
            and Outstanding Payable (customer payouts pending). View these pages to see the accumulated balances.
          </span>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Rule Type Tabs - 4 types of settlement rules */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 24 }}>
        <Tabs
          activeKey={activeRuleType}
          onChange={setActiveRuleType}
          items={[
            {
              key: "all",
              label: (
                <Space>
                  <PlayCircleOutlined />
                  All Runs
                </Space>
              ),
            },
            ...Object.entries(ruleTypeConfig).map(([key, config]) => ({
              key,
              label: (
                <Space>
                  {config.icon}
                  {config.label}
                </Space>
              ),
            })),
          ]}
        />
        {activeRuleType !== "all" && (
          <div style={{ marginTop: 12, padding: "12px 16px", background: token.colorFillQuaternary, borderRadius: 8 }}>
            <Space direction="vertical" size={4}>
              <Text strong>{ruleTypeConfig[activeRuleType]?.label}</Text>
              <Text type="secondary" style={{ fontSize: 13 }}>{ruleTypeConfig[activeRuleType]?.description}</Text>
            </Space>
          </div>
        )}
      </Card>

      {/* Summary Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Today's Run Status"
            value={summaryStats.todayStatus}
            icon={statusConfig[summaryStats.todayStatus]?.icon || <ClockCircleOutlined />}
            color={
              summaryStats.todayStatus === "COMPLETED" ? "rgba(82, 196, 26, 0.1)" :
              summaryStats.todayStatus === "FAILED" ? "rgba(255, 77, 79, 0.1)" :
              summaryStats.todayStatus === "RUNNING" ? "rgba(24, 144, 255, 0.1)" :
              "rgba(140, 140, 140, 0.1)"
            }
            loading={loading}
            extra={
              <Badge
                status={summaryStats.todayStatus === "COMPLETED" ? "success" : summaryStats.todayStatus === "FAILED" ? "error" : "processing"}
                text={<Text type="secondary" style={{ fontSize: 12 }}>{summaryStats.todayCount} runs today</Text>}
              />
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Outstanding Receivable"
            value={outstanding.outstanding_receivable?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            prefix="$"
            icon={<DollarOutlined />}
            color="rgba(82, 196, 26, 0.1)"
            loading={loading}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Platform income pending
              </Text>
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Outstanding Payable"
            value={outstanding.outstanding_payable?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            prefix="$"
            icon={<WalletOutlined />}
            color="rgba(250, 173, 20, 0.1)"
            loading={loading}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Customer payouts pending
              </Text>
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SummaryStatCard
            title="Last Successful Run"
            value={summaryStats.lastSuccessfulRun || "—"}
            icon={<CalendarOutlined />}
            color="rgba(84, 162, 204, 0.1)"
            loading={loading}
            extra={
              outstanding.pending_runs > 0 ? (
                <Badge
                  status="warning"
                  text={<Text type="secondary" style={{ fontSize: 12 }}>{outstanding.pending_runs} pending runs</Text>}
                />
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>All caught up</Text>
              )
            }
          />
        </Col>
      </Row>

      {/* Filters */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16 }}>
        <Space wrap size={16}>
          <Space>
            <Text type="secondary">Date Range:</Text>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              allowClear
            />
          </Space>
          <Space>
            <Text type="secondary">Status:</Text>
            <Select
              style={{ width: 140 }}
              placeholder="All Status"
              allowClear
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              options={Object.entries(statusConfig).map(([key, config]) => ({
                value: key,
                label: config.text,
              }))}
            />
          </Space>
          <Space>
            <Text type="secondary">Run Type:</Text>
            <Select
              style={{ width: 120 }}
              placeholder="All Types"
              allowClear
              value={filters.runType}
              onChange={(value) => setFilters({ ...filters, runType: value })}
              options={Object.entries(runTypeConfig).map(([key, config]) => ({
                value: key,
                label: config.text,
              }))}
            />
          </Space>
        </Space>
      </Card>

      {/* Settlement Runs Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <ClockCircleOutlined style={{ color: token.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Settlement Run History</span>
          </Space>
        }
        extra={
          <Text type="secondary" style={{ fontSize: 13 }}>
            {filteredRuns.length} runs
          </Text>
        }
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: "12px 16px",
            minHeight: 48,
          },
          body: { padding: 0 },
        }}
      >
        <Table
          columns={columns}
          dataSource={filteredRuns}
          rowKey="run_id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
          scroll={{ x: 1550 }}
          rowClassName={(record) =>
            record.status === "FAILED" ? "ant-table-row-error" : ""
          }
        />
      </Card>
    </PageLayout>
  );
}
